import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from '@/utils/production-logger';

interface BackupStatusData {
  last_backup_at: string | null;
  last_backup_eastern: string | null;
  backup_method: string;
  is_mega: boolean;
  is_fallback: boolean;
  mega_db_url: string | null;
  mega_full_url: string | null;
  mega_error: string | null;
  last_failure_at: string | null;
  last_failure_error: string | null;
  next_backup_utc: string | null;
  checked_at: string;
}

// Calculate next 3am ET from now
const getNextBackupTime = (): Date => {
  const now = new Date();
  // 3am ET = 8am UTC (during EST) or 7am UTC (during EDT)
  // Use 8am UTC as standard
  const next = new Date(now);
  next.setUTCHours(8, 0, 0, 0);
  
  // If we're past 8am UTC today, next backup is tomorrow
  if (now.getTime() > next.getTime()) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  
  return next;
};

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.abs(diffMs) / (1000 * 60 * 60);
  const diffDays = Math.abs(diffMs) / (1000 * 60 * 60 * 24);

  if (diffMs < 0) {
    // Past time
    if (diffHours < 1) {
      const minutes = Math.floor(diffHours * 60);
      return `${minutes} min ago`;
    } else if (diffHours < 48) {
      return `${Math.floor(diffHours)} hours ago`;
    } else {
      return `${Math.floor(diffDays)} days ago`;
    }
  } else {
    // Future time
    if (diffHours < 1) {
      const minutes = Math.floor(diffHours * 60);
      return `in ${minutes} min`;
    } else if (diffHours < 48) {
      return `in ${Math.floor(diffHours)} hours`;
    } else {
      return `in ${Math.floor(diffDays)} days`;
    }
  }
};

export const BackupStatusWidget = () => {
  const [backupStatus, setBackupStatus] = useState<BackupStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBackupStatus = async () => {
      try {
        // Use the new comprehensive RPC
        const { data, error: rpcError } = await supabase.rpc('get_backup_status');
        
        if (rpcError) {
          productionLogger.error('Error fetching backup status:', rpcError);
          setError('Failed to load backup status');
          return;
        }
        
        setBackupStatus(data as unknown as BackupStatusData);
        setError(null);
      } catch (err) {
        productionLogger.error('Error fetching backup status:', err);
        setError('Failed to load backup status');
      } finally {
        setLoading(false);
      }
    };

    fetchBackupStatus();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchBackupStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getHealthStatus = () => {
    if (!backupStatus?.last_backup_at) return { status: 'error', message: 'No backups found' };
    
    const lastBackup = new Date(backupStatus.last_backup_at);
    const hoursSince = (Date.now() - lastBackup.getTime()) / (1000 * 60 * 60);
    
    // Check for MEGA vs fallback
    if (backupStatus.is_fallback) {
      return { status: 'warning', message: 'Using Supabase fallback (MEGA failed)' };
    }
    
    if (hoursSince > 28) {
      return { status: 'error', message: 'Backup overdue' };
    } else if (hoursSince > 25) {
      return { status: 'warning', message: 'Backup slightly delayed' };
    }
    
    return { status: 'healthy', message: 'MEGA backup OK' };
  };

  const health = getHealthStatus();

  const getHealthIcon = () => {
    switch (health.status) {
      case 'healthy': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '🔴';
      default: return '⏳';
    }
  };

  const getHealthColor = () => {
    switch (health.status) {
      case 'healthy': return 'text-primary';
      case 'warning': return 'text-amber-500';
      case 'error': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getLastBackupDisplay = () => {
    if (!backupStatus?.last_backup_at) return 'No backups found';
    return formatRelativeTime(new Date(backupStatus.last_backup_at));
  };

  const getNextBackupDisplay = () => {
    return formatRelativeTime(getNextBackupTime());
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Backup Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Backup Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">
            <div className="flex items-center gap-2 mb-2">
              <span>🔴</span>
              <span>Error loading status</span>
            </div>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Backup Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Last Backup:</span>
          <span className="font-medium">{getLastBackupDisplay()}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Next Backup:</span>
          <span className="font-medium">{getNextBackupDisplay()} (3am ET)</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Destination:</span>
          <span className={`font-medium ${backupStatus?.is_mega ? 'text-primary' : 'text-amber-500'}`}>
            {backupStatus?.is_mega ? '☁️ MEGA' : '⚠️ Supabase (fallback)'}
          </span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Health:</span>
          <div className="flex items-center gap-2">
            <span className={getHealthColor()}>{getHealthIcon()}</span>
            <span className={`font-medium ${getHealthColor()}`}>
              {health.message}
            </span>
          </div>
        </div>

        {backupStatus?.is_fallback && backupStatus?.mega_error && (
          <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-600">
            MEGA Error: {backupStatus.mega_error}
          </div>
        )}

        {backupStatus?.last_failure_at && (
          <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
            Last failure: {formatRelativeTime(new Date(backupStatus.last_failure_at))}
            {backupStatus.last_failure_error && ` - ${backupStatus.last_failure_error}`}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
